import { FormBuilderRef } from '@tn/formbuilder/hooks/useFormBuilder';
import { PAGE_TYPES } from '@tn/foundation/utils/constants/tracking';
import {
    AnyEventObject,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    OutputFrom,
    setup,
} from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { devLog } from '../../utils/devLog';
import { ROUTE_CHANGE } from '../helpers';
import { resolveRegionalizedUrl } from '../rendezvous/resolveRegionalizedUrl';
import {
    getRendezvousAppLogFromSubmitError,
    trackActivateButtonClickEvent,
    trackActivationFailureEvent,
} from '../rendezvous/tracking';
import type { DeviceInfo, RendezvousFlowContext, TvOutput } from '../rendezvous/types';
import { DEFAULT_ACTIVATION_SCREEN } from '../rendezvous/types';

import { submitFormTvActor } from './submitFormTvActor';
// --- Types ---

export type TvFlowContext = {
    activationDidFail?: boolean;
    doRoutePushOnEntry?: boolean;
    formBuilderRef?: FormBuilderRef;
    formError: string | null;
    lookupOnly?: boolean;
    url: string;
} & Partial<DeviceInfo>;

export { submitFormTvActor, submitFormTvInitActor } from './submitFormTvActor';

/**
 * Invoke input (merged blueprint `input` + `inputActor`):
 * TvBlueprintInput: from jsonc blueprint "input" field
 * TvInputActorInput: from inputActor
 * TvInput: merged input
 */
type TvBlueprintInput = Pick<TvFlowContext, 'url' | 'lookupOnly'> & {
    includeRegionInUrl?: boolean;
};
type TvInputActorInput = Pick<TvFlowContext, 'activationDidFail' | 'doRoutePushOnEntry'> & {
    defaultRegionCode: string;
};
type TvInput = TvBlueprintInput & TvInputActorInput;

type SubmittingActorOutput = OutputFrom<ReturnType<typeof submitFormTvActor.initActor>>;
type TvFlowEvents =
    | { type: 'CONNECT_FORMBUILDER_REF'; params: FormBuilderRef }
    | { type: 'SUBMIT_CLICK'; params: TvFlowContext }
    | { type: 'SUBMITTING' }
    | DoneActorEvent<SubmittingActorOutput, 'submitting'>
    | ErrorActorEvent<SubmittingActorOutput, 'submitting'>;

// --- TV flow entity ---

export const tvFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, region, router } = rawParams as InitMachineParams;

        return setup({
            types: {
                context: {} as TvFlowContext,
                events: {} as TvFlowEvents,
                input: {} as TvInput,
            },
            guards: {},
            actions: {
                assignFormBuilderRef: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'CONNECT_FORMBUILDER_REF');
                    enqueue.assign({ formBuilderRef: event.params });
                    devLog('[tvFlow] assignFormBuilderRef', () => [
                        event.params,
                        context.activationDidFail,
                    ]);

                    // display activation error once if failed
                    if (event.params && context.activationDidFail) {
                        enqueue.assign({ activationDidFail: false });

                        const formBuilderRef = event.params;
                        formBuilderRef?.toggleLoading(false);
                        formBuilderRef?.toggleBanner({
                            message: 'something_went_wrong_please_try_again',
                            variant: 'critical',
                        });
                    }
                }),
                clearLoadingScreen: enqueueActions(({ context, event }) => {
                    assertEvent(event, 'xstate.done.actor.submitting');
                    context.formBuilderRef?.toggleLoading(false);
                }),
                submitPin: enqueueActions(({ enqueue, event, context }) => {
                    context.formBuilderRef?.toggleLoading(true);
                    assertEvent(event, 'SUBMIT_CLICK');
                    enqueue.assign({ activationCode: event.params.activationCode });
                    enqueue.raise({ type: 'SUBMITTING' });
                }),
                trackActivateButtonClick: ({ event }) => {
                    devLog('[tvFlow] trackActivateButtonClick', () => event);
                    assertEvent(event, 'xstate.done.actor.submitting');

                    trackActivateButtonClickEvent({
                        activationCode: event.output.activationCode,
                        activationScreen:
                            event.output.activationScreen || DEFAULT_ACTIVATION_SCREEN,
                        device: event.output.device,
                    });
                },
                setDeviceInfo: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.submitting');

                    const output = event.output;
                    const device = output?.device;

                    devLog('[tvFlow] setDeviceInfo output', () => output);

                    enqueue.assign({
                        activationCode: event.output.activationCode,
                        activationScreen:
                            event.output.activationScreen || DEFAULT_ACTIVATION_SCREEN,
                        device,
                    });
                }),
                goToTvRoute: ({ context }) => {
                    devLog('[tvFlow] goToTvRoute', () => context.doRoutePushOnEntry);
                    if (context.doRoutePushOnEntry) {
                        devLog('[tvFlow] goToTvRoute did route push');
                        router.push(context.url);
                    }
                },
                handleError: ({ context }) => {
                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleError('An error occurred');
                },
                handleErrorWithBanner: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');

                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleBanner({
                        message: 'invalid_rendezvous_code_msg',
                        variant: 'critical',
                    });
                },
                trackActivationFailure: ({ context, event }) => {
                    devLog('[tvFlow] trackActivationFailure', () => event);
                    assertEvent(event, 'xstate.error.actor.submitting');

                    trackActivationFailureEvent({
                        activationCode: context.activationCode,
                        pageType: PAGE_TYPES.RENDEZVOUS_ACTIVATION_ENTER_CODE,
                        ...getRendezvousAppLogFromSubmitError(event.error),
                    });
                },
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: ({ input }) => {
                devLog('[tvFlow] createMachine input', () => input);

                return {
                    activationCode: '',
                    activationScreen: undefined,
                    formBuilderRef: undefined,
                    formError: null,
                    ...input,
                    url: resolveRegionalizedUrl(region, input),
                };
            },
        });
    },
    inputActor: ({
        context,
        event,
    }: {
        context: RendezvousFlowContext;
        event: AnyEventObject;
    }): TvInputActorInput => {
        const payload: TvInputActorInput = {
            defaultRegionCode: context.defaultRegionCode,
            // Entered by reading the URL (start or a completed route change): already there, no push.
            doRoutePushOnEntry: event.type !== 'xstate.init' && event.type !== ROUTE_CHANGE,
        };

        if (event.type === 'xstate.done.actor.tvComplete' && event.output.activationDidFail) {
            payload.activationDidFail = true;
            payload.doRoutePushOnEntry = true;
        }

        return payload;
    },
    outputActor: ({ context }: { context: TvFlowContext }): TvOutput => {
        const output: TvOutput = {
            activationCode: context.activationCode ?? '',
            activationScreen: context.activationScreen ?? DEFAULT_ACTIVATION_SCREEN,
            device: {
                name: context.device?.name ?? '',
                partner: context.device?.partner ?? '',
            },
        };
        devLog('[tvFlow] outputActor', () => output);

        return output;
    },
};
